// Brand Management Component
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment,
  Alert,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Tab,
  Tabs,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  MoreVert,
  Inventory,
  Refresh,
  Business,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import apiService from '../../services/api';
import authService from '../../services/auth';

const normalizeBrand = (brand) => ({
  ...brand,
  product_count:
    typeof brand?.product_count === 'string'
      ? Number(brand.product_count) || 0
      : brand?.product_count || 0,
  status:
    typeof brand?.status === 'string' && brand.status.trim() !== ''
      ? brand.status.trim()
      : 'active',
});



const BrandManagement = () => {
  const [brands, setBrands] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0: All, 1: Custom, 2: Default, 3: Hidden, 4: Active
  const [togglingBrandId, setTogglingBrandId] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      let endpoint = '/brands';

      // Tabs: 0=All, 1=Active, 2=Inactive
      if (activeTab === 1) endpoint = '/brands?status=active';
      else if (activeTab === 2) endpoint = '/brands?status=inactive';
      
      const response = await apiService.get(endpoint);
      
      if (response.success) {
        const raw = response.data?.brands || response.data || [];
        const normalized = Array.isArray(raw) ? raw.map(normalizeBrand) : [];
        setBrands(normalized);
      }
    } catch (err) {
      setError('Error fetching brands: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const isAdminLike = (user) => String(user?.role_name || "").toLowerCase().includes("admin");

  const hasPermission = (module, action = "view") => {
    const user = authService.getUser();
    if (isAdminLike(user)) return true;
    if (!userPermissions) return false;
    return userPermissions?.[module]?.[action] === true;
  };

  const fetchPermissions = async () => {
    try {
      const user = authService.getUser();
      if (!user?.id) return;
      const response = await apiService.get(`/roles/user/${user.id}/permissions`);
      setUserPermissions(response.data || {});
    } catch (e) {
      // safest fallback: no permissions (so no edit/delete/add)
      setUserPermissions({});
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await apiService.get('/brands/statistics');
      if (response.success) {
        setStatistics(response.data.statistics || {});
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      await fetchData();
      await fetchStatistics();
      await fetchPermissions();
    };
    loadInitialData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const filteredBrands = brands.filter(brand =>
    brand.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (brand.description && brand.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleOpenDialog = (brand = null) => {
    // Permission checks (UI-level). API also enforces this.
    if (brand && !hasPermission("brands", "edit")) {
      setError("Permission denied");
      return;
    }
    if (!brand && !hasPermission("brands", "add")) {
      setError("Permission denied");
      return;
    }

    if (brand) {
      setEditingBrand(brand);
      setFormData({
        name: brand.name,
        description: brand.description || '',
      });
    } else {
      setEditingBrand(null);
      setFormData({
        name: '',
        description: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBrand(null);
  };

  const handleInputChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async () => {
    try {
      // Permission checks (UI-level). API also enforces this.
      if (editingBrand && !hasPermission("brands", "edit")) {
        setError("Permission denied");
        return;
      }
      if (!editingBrand && !hasPermission("brands", "add")) {
        setError("Permission denied");
        return;
      }

      let response;
      if (editingBrand) {
        response = await apiService.put(`/brands/${editingBrand.id}`, formData);
      } else {
        response = await apiService.post('/brands', formData);
      }

      if (response.success) {
        fetchData();
        fetchStatistics();
        handleCloseDialog();
        setError('');
      } else {
        setError(response.message || 'Failed to save brand');
      }
    } catch (err) {
      setError('Error saving brand: ' + err.message);
    }
  };

  const handleMenuOpen = (event, brand) => {
    setAnchorEl(event.currentTarget);
    setSelectedBrand(brand);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBrand(null);
  };

  const handleDeleteBrand = async () => {
    // Permission checks (UI-level). API also enforces this.
    if (!hasPermission("brands", "delete")) {
      setError("Permission denied");
      handleMenuClose();
      return;
    }

    if (selectedBrand) {
      try {
        const response = await apiService.delete(`/brands/${selectedBrand.id}`);
        if (response.success) {
          fetchData();
          fetchStatistics();
          setError('');
        } else {
          setError(response.message || 'Failed to delete brand');
        }
      } catch (err) {
        setError('Error deleting brand: ' + err.message);
      }
    }
    handleMenuClose();
  };

  const handleToggleVisibility = async (brand) => {
    // Permission checks (UI-level). API also enforces this.
    if (!hasPermission("brands", "edit")) {
      setError("Permission denied");
      return;
    }

    try {
      setTogglingBrandId(brand.id);
      
      // Optimistic update - immediately update the UI
      const updatedBrands = brands.map(b => 
        b.id === brand.id 
          ? { ...b, status: b.status === 'inactive' ? 'active' : 'inactive' }
          : b
      );
      setBrands(updatedBrands);

      const response = await apiService.post('/brands/toggle-status', {
        brand_id: brand.id
      });
      
      if (response.success) {
        // Update with server response to ensure consistency
        const serverUpdatedBrands = brands.map(b => 
          b.id === brand.id 
            ? { ...b, status: response.data.status || b.status }
            : b
        );
        setBrands(serverUpdatedBrands);
        
        // Only fetch statistics, not the full data
        fetchStatistics();
        setError('');
      } else {
        // Revert optimistic update on error
        setBrands(brands);
        setError(response.message || 'Failed to toggle brand visibility');
      }
    } catch (err) {
      // Revert optimistic update on error
      setBrands(brands);
      setError('Error toggling brand visibility: ' + err.message);
    } finally {
      setTogglingBrandId(null);
    }
  };

  const getBrandStatusChip = (brand) => {
    const isActive = (brand.status || 'active') !== 'inactive';
    return (
      <Chip
        icon={isActive ? <Visibility /> : <VisibilityOff />}
        label={isActive ? 'Active' : 'Inactive'}
        color={isActive ? 'success' : 'default'}
        variant="outlined"
        size="small"
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading brands...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Brands
        </Typography>
        <Box display="flex" gap={2}>
          {hasPermission("brands", "add") && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add 
          </Button>
          )}
        </Box>
      </Box>



      {/* Statistics Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { 
          xs: 'repeat(2, 1fr)', 
          sm: 'repeat(4, 1fr)' 
        }, 
        gap: 3, 
        mb: 3 
      }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Business sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Total
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {statistics.total_brands || 0}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Visibility sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Active
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {statistics.active_brands || 0}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <VisibilityOff sx={{ fontSize: 40, color: 'text.secondary', mr: 2 }} />
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Inactive
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {statistics.inactive_brands || 0}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Inventory sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Products
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {statistics.brands_with_products || 0}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="All Brands" />
          <Tab label="Active Brands" />
          <Tab label="Inactive Brands" />
        </Tabs>
        
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search brands by name or description..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Brands Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Brand</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Products</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBrands.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Box py={4}>
                    <Business sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      {searchTerm ? 'No brands found matching your search' : 
                       activeTab === 2 ? 'No inactive brands' : 
                       activeTab === 1 ? 'No active brands' : 'No brands yet'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {searchTerm ? 'Try adjusting your search terms' : 
                       activeTab === 2 ? 'Inactive brands are hidden from your product forms' :
                       activeTab === 1 ? 'These are the brands currently available in your product forms' : 
                       'Add your first custom brand to get started'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredBrands.map((brand) => (
                <TableRow key={brand.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {brand.name}
                      </Typography>
                      {brand.description && (
                        <Typography variant="body2" color="text.secondary">
                          {brand.description.length > 50 
                            ? `${brand.description.substring(0, 50)}...` 
                            : brand.description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {getBrandStatusChip(brand)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {brand.product_count || 0} products
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={1}>
                      {/* Visibility toggle for all brands */}
                      <Tooltip title={(brand.status || 'active') !== 'inactive' ? "Deactivate brand" : "Activate brand"}>
                        <IconButton 
                          onClick={() => handleToggleVisibility(brand)}
                          color={(brand.status || 'active') !== 'inactive' ? "primary" : "default"}
                          disabled={togglingBrandId === brand.id || !hasPermission("brands", "edit")}
                          size="small"
                          sx={{
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'scale(1.1)',
                            }
                          }}
                        >
                          {togglingBrandId === brand.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Box sx={{ 
                              transition: 'all 0.2s ease-in-out',
                              transform: (brand.status || 'active') !== 'inactive' ? 'rotate(0deg)' : 'rotate(180deg)'
                            }}>
                              {(brand.status || 'active') !== 'inactive' ? <Visibility /> : <VisibilityOff />}
                            </Box>
                          )}
                        </IconButton>
                      </Tooltip>
                      
                      {(hasPermission("brands", "edit") || hasPermission("brands", "delete")) && (
                        <IconButton onClick={(e) => handleMenuOpen(e, brand)}>
                          <MoreVert />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {hasPermission("brands", "edit") && (
          <MenuItem onClick={() => { handleOpenDialog(selectedBrand); handleMenuClose(); }}>
            <Edit sx={{ mr: 1 }} /> Edit
          </MenuItem>
        )}
        {hasPermission("brands", "delete") && (
          <MenuItem onClick={handleDeleteBrand} sx={{ color: 'error.main' }}>
            <Delete sx={{ mr: 1 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseDialog();
          }
        }} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingBrand ? 'Edit Custom Brand' : 'Add New Custom Brand'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2, color: "error.main" }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            
            <TextField
              fullWidth
              label="Brand Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Description"
              name="description"
              multiline
              rows={3}
              value={formData.description}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
            

            

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingBrand ? 'Update' : 'Add'} Brand
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrandManagement;
